import { FC } from "react";
import { Link } from "react-router-dom";
import { WorkDTO } from "../../../../../domain/models/dto/WorkDTO";
import WorkLabels from "../WorkLabels";
import { getNormalizedDate, getNormalizedTime } from "../../../../../tools/DatetimeTools";
import CurrencyIconSVG from "../../../svg/CurrencyIconSVG";
import ClockIconSVG from "../../../svg/ClockIconSVG";
import ContactsPeopleIconSVG from "../../../svg/ContactsPeopleIconSVG";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import LocationIconSVG from "../../../svg/LocationIconSVG";
import { getLocalizedApplicationCreatedTime, getWorkDistance } from "../../../../../slices/works-slice";
import { WorkListType } from "../../../../../domain/models/Work";
import { WorkStatus } from "../../../../../domain/enums/WorkStatusEnum";
import CurrencyAmount from "../../CurrencyAmount";

interface WorkCardWithApplicationProps {
    work: WorkDTO;
    type: WorkListType;
    className?: string;
}

const WorkCardWithApplication: FC<WorkCardWithApplicationProps> = (props) => {
    const { t } = useTranslation();

    const comment = _.get(props.work, "userApplication.comment");
    const deleted = props.work.status === WorkStatus.DELETED;

    return (
        <div className={`relative ${props.className}`}>
            <Link
                to={`/work/${props.work.id}`}
                className={`relative ${
                    deleted ? "cursor-not-allowed" : "cursor-pointer"
                } flex flex-col bg-backgroundInput rounded-[10px] py-9 td4:py-8 tmax4:py-5 px-10 td4:px-5 tmax4:px-4 max-w-[845px]`}
                onClick={(e) => {
                    if (deleted) {
                        e.preventDefault();
                    }
                }}
            >
                <div className="flex justify-between mb-8 dmax2:mb-5">
                    <WorkLabels
                        work={props.work}
                        biggerVersion
                        showPersonalApplicationStatus
                    />
                </div>
                {!deleted && (
                    <div className="flex flex-col items-start w-full mb-8 tmax4:mb-5">
                        <p className="dmin2:H24B td4:H24B tmax4:H20B text-textWhite text-start mb-4 tmax4:mb-3">
                            {props.work.title}
                        </p>
                        <div className="flex">
                            <p className="H20 dmax2:H16 text-textWhite text-left underline decoration-1 underline-offset-[4px]">
                                {props.work.address.city}, {props.work.address.address} {getWorkDistance(props.work, t)}
                            </p>
                            <LocationIconSVG size={24} className="min-w-6 min-h-6 ml-3 text-textWhite" />
                        </div>
                        <div className="flex items-end mt-4">
                            <span className="H20 dmax2:H16 text-textFaded">{t("work.workStart")}</span>
                            <span className="H20B dmax2:H14B text-textWhite ml-[18px] dmax2:ml-2">
                                {getNormalizedDate(props.work.startFrom)}
                            </span>
                            <span className="H20 dmax2:H14B text-textWhite ml-5 dmax2:ml-3">
                                {getNormalizedTime(props.work.startFrom)}
                            </span>
                        </div>
                    </div>
                )}
                {comment && (
                    <div className="flex flex-col items-start w-full mb-8 tmax4:mb-5">
                        <p className="H20 dmax2:H16 text-textWhite text-left">{comment}</p>
                    </div>
                )}
                <div className="flex flex-wrap gap-4 dmax2:gap-2 max-w-[calc(100%-32px)]">
                    <div className="H16 flex justify-center items-center dmax2:H14 text-textWhite bg-primaryBlue rounded-[30px] w-fit tmax5:h-[30px] px-4 dmax2:px-2.5 py-2 dmax2:py-[7px]">
                        <CurrencyIconSVG className="w-6 dmax2:w-4 h-6 dmax2:h4 mr-3 dmax2:mr-1.5 text-textWhite" />
                        <span>
                            <CurrencyAmount price={props.work.price} suffix={t("work.applicationCreation.priceUnits")} />
                        </span>
                    </div>
                    <div className="H16 flex justify-center items-center dmax2:H14 text-textWhite bg-primaryBlue rounded-[30px] w-fit tmax5:h-[30px] px-4 dmax2:px-2.5 py-2 dmax2:py-[7px]">
                        <ClockIconSVG className="w-6 dmax2:w-4 h-6 dmax2:h4 mr-3 dmax2:mr-1.5 text-textWhite" />
                        <span>
                            {props.work.estimatedHours} {t("work.estimatedHours")}
                        </span>
                    </div>
                    <div className="H16 flex justify-center items-center dmax2:H14 text-textWhite bg-primaryBlue rounded-[30px] w-fit tmax5:h-[30px] px-4 dmax2:px-2.5 py-2 dmax2:py-[7px]">
                        <ContactsPeopleIconSVG className="w-6 dmax2:w-4 h-6 dmax2:h4 mr-3 dmax2:mr-1.5 text-textWhite" />
                        <span>
                            {props.work.workersAmount} {t("work.workersAmount")}
                        </span>
                    </div>
                </div>
                <span className="H20 dmax2:H16 text-textFaded mt-4 w-full text-end">
                    {getLocalizedApplicationCreatedTime(props.work.userApplication)}
                </span>
            </Link>
        </div>
    );
};

export default WorkCardWithApplication;
